import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { apiCall } from "../Apis/apiCall";
import LoadingComponent from "./ui/LoadingComponent";
import Avatar from "react-avatar";
import { Icon } from "@iconify/react/dist/iconify.js";
import ErrorAlert from "./ui/ErrorAlert";

const ViewClientLayer = () => {
  const { id } = useParams(); // Get client_id from URL
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const fetchClientDetails = async (clientId) => {
    try {
      setLoading(true);
      setError("");
      const response = await apiCall(
        "get",
        `/get-client-by-id/${clientId}`,
        null,
        navigate
      );
      if (response?.data?.success) {
        setClient(response.data.data);
      } else {
        setError("Failed to load client details.");
      }
    } catch (error) {
      console.error("Error fetching client details:", error);
      setError(error?.message || "Error fetching client details.");
    } finally {
      setLoading(false);
    }
  };

  const getClientLogo = (url, clientName) => {
    if (url && url.trim() !== "") {
      return `https://pevbackend.codovio.com/public/uploads/client_logos/${url}`;
    }
    return null;
  };

  useEffect(() => {
    fetchClientDetails(id);
  }, [id]);

  if (error) {
    return <ErrorAlert />;
  }

  return (
    <div className="p-4 p-md-3 p-sm-2">
      {error && (
        <div
          className="alert alert-danger alert-dismissible fade show mb-4"
          role="alert"
          style={{ borderRadius: "8px" }}
        >
          <Icon
            icon="material-symbols:error"
            className="me-3"
            style={{ fontSize: "20px" }}
          />
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError("")}
          ></button>
        </div>
      )}

      {loading ? (
        <div className="text-center py-5">
          <LoadingComponent />
          <p className="mt-3 text-muted"></p>
        </div>
      ) : client ? (
        <>
          {/* Hero Client Card */}
          <div
            className="card shadow-sm mb-4"
            style={{ border: "none", borderRadius: "8px" }}
          >
            <div className="card-body p-4">
              {/* Back Button */}
              <div className=" d-flex justify-content-end gap-2">
                <button
                  className="btn btn-sm btn-primary-600 d-flex align-items-center"
                  onClick={() => navigate("/all-clients")}
                  style={{
                    borderRadius: "8px",
                    padding: "6px 12px",
                    transition: "all 0.3s ease",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.transform = "scale(1.05)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.transform = "scale(1)")
                  }
                >
                  Back To Clients
                </button>
              </div>

              <div className="text-center mb-4">
                {/* Large Avatar/Logo - Centered */}
                <div className="d-flex justify-content-center mb-3">
                  {client.logo_url && client.logo_url.trim() !== "" ? (
                    <img
                      loading="lazy"
                      src={`https://pevbackend.codovio.com/public/uploads/client_logos/${client.logo_url}`}
                      alt={client.client_name || "Client Logo"}
                      style={{
                        width: "120px",
                        height: "120px",
                        objectFit: "cover",
                        borderRadius: "50%",
                        border: "2px solid",
                      }}
                      className="shadow-sm"
                      onError={(e) => {
                        e.target.style.display = "none";
                        e.target.nextSibling.style.display = "block";
                      }}
                    />
                  ) : null}
                  <Avatar
                    name={client.client_name || "N/A"}
                    size="120"
                    round={true}
                    style={{
                      display:
                        client.logo_url && client.logo_url.trim() !== ""
                          ? "none"
                          : "block",
                      border: "2px solid",
                    }}
                  />
                </div>

                {/* Client Name & Status */}
                <h4
                  className="text-primary-600 fw-bold mb-2"
                  style={{ fontWeight: "600" }}
                >
                  {client.client_name || "N/A"}
                </h4>
                <p className="text-muted mb-3">{client.client_type}</p>

                <div className="d-flex justify-content-center">
                  <span
                    className={`px-3 py-1 fw-medium text-sm d-inline-flex align-items-center ${
                      client.is_active
                        ? "bg-success-focus text-success-600 border border-success-main"
                        : "bg-danger-focus text-danger-600 border border-danger-main"
                    }`}
                    style={{ borderRadius: "8px" }}
                  >
                    <Icon
                      icon={
                        client.is_active
                          ? "material-symbols:check-circle"
                          : "material-symbols:cancel"
                      }
                      className="me-2"
                      style={{ fontSize: "16px" }}
                    />
                    {client.is_active ? "Active Account" : "Inactive Account"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Information Grid */}
          <div className="row g-3">
            {/* Contact Information */}
            <div className="col-lg-6 col-md-6">
              <div
                className="card shadow-sm h-100"
                style={{ border: "none", borderRadius: "8px" }}
              >
                <div className="card-body p-3 p-sm-2">
                  <div className="d-flex align-items-center mb-3">
                    <div className="p-2 bg-primary-focus rounded-circle me-3">
                      <Icon
                        icon="material-symbols:contact-mail"
                        className="text-primary-600"
                        style={{ fontSize: "20px" }}
                      />
                    </div>
                    <h6 className="fw-medium text-primary-600 mb-0">
                      Contact Details
                    </h6>
                  </div>
                  <div className="row g-2">
                    {/* <div className="col-12">
                      <div className="p-2">
                        <div className="d-flex align-items-start">
                          <Icon icon="material-symbols:email" className="text-primary-600 me-3 mt-1" style={{ fontSize: "18px", minWidth: "18px" }} />
                          <div className="flex-grow-1">
                            <label className="fw-medium text-primary-600 d-block">Email Address</label>
                            <p className="mb-0 text-break" style={{ wordBreak: "break-word", overflowWrap: "break-word" }}>{client.email}</p>
                          </div>
                        </div>
                      </div>
                    </div> */}
                    <div className="col-12">
                      <div className="p-2">
                        <div className="d-flex align-items-start">
                          <Icon
                            icon="material-symbols:mail-outline"
                            className="text-success-600 me-3 mt-1"
                            style={{ fontSize: "18px", minWidth: "18px" }}
                          />

                          <div className="flex-grow-1">
                            <label className="fw-medium text-primary-600 d-block">
                              Email Address
                            </label>
                            <p
                              className="mb-0 text-break"
                              style={{
                                wordBreak: "break-word",
                                overflowWrap: "break-word",
                              }}
                            >
                              {client.email}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="p-2">
                        <div className="d-flex align-items-start">
                          <Icon
                            icon="material-symbols:call"
                            className="text-success-600 me-3 mt-1"
                            style={{ fontSize: "18px", minWidth: "18px" }}
                          />
                          <div className="flex-grow-1">
                            <label className="fw-medium text-primary-600 d-block">
                              Phone Number
                            </label>
                            <p className="mb-0">{client.phone_no}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Plan Information */}
            <div className="col-lg-6 col-md-6">
              <div
                className="card shadow-sm h-100"
                style={{ border: "none", borderRadius: "8px" }}
              >
                <div className="card-body p-3 p-sm-2 d-flex flex-column">
                  <div className="d-flex align-items-center mb-3">
                    <div className="p-2 bg-warning-focus rounded-circle me-3">
                      <Icon
                        icon="material-symbols:workspace-premium"
                        className="text-warning-600"
                        style={{ fontSize: "20px" }}
                      />
                    </div>
                    <h6 className="fw-medium text-primary-600 mb-0">
                      Subscription Plan
                    </h6>
                  </div>
                  <div className="text-center flex-grow-1 d-flex flex-column justify-content-center">
                    <div className="p-3">
                      <Icon
                        icon="material-symbols:stars"
                        className="text-warning-600 mb-2"
                        style={{ fontSize: "28px" }}
                      />
                      <h6 className="fw-medium text-warning-600 mb-1">
                        {client.plan_name}
                      </h6>
                      <p className=" mb-0 small">Current Active Plan</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Trial Period */}
            <div className="col-lg-6 col-md-6">
              <div
                className="card shadow-sm h-100"
                style={{ border: "none", borderRadius: "8px" }}
              >
                <div className="card-body p-3 p-sm-2">
                  <div className="d-flex align-items-center mb-3">
                    <div className="p-2 bg-info-focus rounded-circle me-3">
                      <Icon
                        icon="material-symbols:schedule"
                        className="text-info-600"
                        style={{ fontSize: "20px" }}
                      />
                    </div>
                    <h6 className="fw-medium text-primary-600 mb-0">
                      Trial Period
                    </h6>
                  </div>
                  <div className="row g-2">
                    <div className="col-6">
                      <div className="text-center p-2">
                        <Icon
                          icon="material-symbols:play-arrow"
                          className="text-info-600 mb-2"
                          style={{ fontSize: "20px" }}
                        />
                        <label className="fw-medium text-primary-600 d-block small">
                          Start Date
                        </label>
                        <p className="mb-0 text-sm">
                          {client.trial_start_date
                            ? new Date(
                                client.trial_start_date
                              ).toLocaleDateString("en-GB", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              })
                            : "N/A"}
                        </p>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="text-center p-2">
                        <Icon
                          icon="material-symbols:stop"
                          className="text-danger-600 mb-2"
                          style={{ fontSize: "20px" }}
                        />
                        <label className="fw-medium text-primary-600 d-block small">
                          End Date
                        </label>
                        <p className="mb-0 text-sm">
                          {client.trial_end_date
                            ? new Date(
                                client.trial_end_date
                              ).toLocaleDateString("en-GB", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              })
                            : "N/A"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Account Information */}
            <div className="col-lg-6 col-md-6">
              <div
                className="card shadow-sm h-100"
                style={{ border: "none", borderRadius: "8px" }}
              >
                <div className="card-body p-3 p-sm-2">
                  <div className="d-flex align-items-center mb-3">
                    <div className="p-2 bg-secondary-focus rounded-circle me-3">
                      <Icon
                        icon="material-symbols:account-circle"
                        className="text-secondary-600"
                        style={{ fontSize: "20px" }}
                      />
                    </div>
                    <h6 className="fw-medium text-primary-600 mb-0">
                      Account Details
                    </h6>
                  </div>
                  <div className="row g-2">
                    <div className="col-12">
                      <div className="p-2">
                        <div className="d-flex align-items-start">
                          <Icon
                            icon="material-symbols:person-add"
                            className="text-primary-600 me-3 mt-1"
                            style={{ fontSize: "18px", minWidth: "18px" }}
                          />
                          <div className="flex-grow-1">
                            <label className="fw-medium text-primary-600 d-block">
                              Created By
                            </label>
                            <p className="mb-0">{client.created_by || "N/A"}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="p-2">
                        <div className="d-flex align-items-start">
                          <Icon
                            icon="material-symbols:calendar-today"
                            className="text-success-600 me-3 mt-1"
                            style={{ fontSize: "18px", minWidth: "18px" }}
                          />
                          <div className="flex-grow-1">
                            <label className="fw-medium text-primary-600 d-block">
                              Account Created
                            </label>
                            <p className="mb-0">
                              {client.created_datetime
                                ? new Date(
                                    client.created_datetime
                                  ).toLocaleDateString("en-GB", {
                                    day: "2-digit",
                                    month: "long",
                                    year: "numeric",
                                  })
                                : "N/A"}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="text-center py-5">
          <div
            className="card shadow-sm"
            style={{ border: "none", borderRadius: "8px" }}
          >
            <div className="card-body py-5">
              <div className="p-4 rounded-circle bg-light d-inline-flex align-items-center justify-content-center mb-3">
                <Icon
                  icon="material-symbols:error"
                  style={{ fontSize: "48px" }}
                  className="text-muted"
                />
              </div>
              <h5 className="fw-medium text-primary-600 mb-2">
                No Client Data Found
              </h5>
              <p className="text-muted">
                The requested client information could not be retrieved. Please
                verify the client details and try again.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewClientLayer;
